export interface ApiResult<T = Record<string, unknown>> {
    ok: boolean
    status: number
    data: T & { error?: string, field?: string, message?: string }
}

export const apiFetch = async <T = Record<string, unknown>>(url: string, body?: unknown, method: 'POST' | 'PUT' | 'DELETE' | 'GET' = 'POST'): Promise<ApiResult<T>> => {
    try {
        const response = await fetch(url, {
            method,
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'X-Stayvora': '1'
            },
            body: method === 'GET' || body === undefined ? undefined : JSON.stringify(body)
        })

        const data = await response.json().catch(() => ({})) as ApiResult<T>['data']

        return { ok: response.ok, status: response.status, data }
    }
    catch {
        return { ok: false, status: 0, data: { error: 'network' } as ApiResult<T>['data'] }
    }
}

export const readFileAsBase64 = (file: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => {
            const result = String(reader.result ?? '')

            resolve(result.includes(',') ? result.split(',')[1] : result)
        }
        reader.onerror = () => reject(reader.error)
        reader.readAsDataURL(file)
    })

// Shrinks a photo in the browser before upload so pages stay fast on mobile.
export const resizeImage = (file: File, maxSize = 2000, quality = 0.86): Promise<Blob> =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const image = new Image()

        image.onload = () => {
            URL.revokeObjectURL(url)

            const scale = Math.min(1, maxSize / Math.max(image.naturalWidth, image.naturalHeight))
            const width = Math.max(1, Math.round(image.naturalWidth * scale))
            const height = Math.max(1, Math.round(image.naturalHeight * scale))
            const canvas = document.createElement('canvas')

            canvas.width = width
            canvas.height = height

            const context = canvas.getContext('2d')

            if (!context) {
                reject(new Error('canvas'))

                return
            }

            context.fillStyle = '#ffffff'
            context.fillRect(0, 0, width, height)
            context.drawImage(image, 0, 0, width, height)
            canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('encode'))), 'image/jpeg', quality)
        }

        image.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error('decode'))
        }

        image.src = url
    })
